import React, {useEffect, useMemo, useState} from "react";
import { DataTableFoot, SortButton } from "../../../components/common/Tables/DataTableParts";
import { AppDispatch } from "../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import RootState from "../../../redux/states/root.state";
import { fetchSpaHistory } from "../../../redux/actions/spaHistory.actions";
import {clearSpaHistory} from "../../../redux/reducers/spaHistory.reducer";
import ReportLoadingProgress from "./ReportLoadingProgress";
import { filterStateMapper, PumpState, SBConState, V1ElectrodePolarity, V1ElectrodeState } from "../reportEnums.enums";
import { ActiveCycleStyleMap } from "../../../mqttData/live/heatCycle.enums";
import { SmartPHStateTextMap } from "../../../mqttData/live/smartPhState.enums";
import { useReportTimeFormat } from "../reportTime";

interface ReportsTableProps {
    spa_guid: string;
    category: string;
    selected: any[];
    startDate: string;
    endDate: string;
    /** Fixed columns, in order and under these headers, instead of every key A-Z. */
    columns?: { key: string; label: string }[];
}

type Order = 'asc' | 'desc';

// A cell with no reading at all. `0` and `false` are real values, not blanks.
const isBlank = (value: any) =>
    value === null || value === undefined || value === '' ||
    (typeof value === 'number' && Number.isNaN(value));

// Compares by type rather than lexically: booleans as OFF < ON, anything
// number-like numerically (so "9" < "10"), everything else as text.
const compareValues = (a: any, b: any): number => {
    if (typeof a === 'boolean' || typeof b === 'boolean') {
        return Number(a) - Number(b);
    }

    const numA = Number(a);
    const numB = Number(b);
    if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
        return numA - numB;
    }

    return String(a).localeCompare(String(b), undefined, {numeric: true, sensitivity: 'base'});
};

const ReportsTable: React.FC<ReportsTableProps> = ({selected, startDate, endDate, spa_guid, category, columns}) => {
    const dispatch: AppDispatch = useDispatch();
    const {data: history, loading, lastQuery} = useSelector((state: RootState) => state.spaHistory);
    const formatTime = useReportTimeFormat();
    // Table Sort
    const [order, setOrder] = useState<Order>('desc');
    const [orderBy, setOrderBy] = useState('timestamp');
    const [headers, setHeaders] = useState<any[]>([]);
    const [page, setPage] = useState(1);
    const rowsPerPage = 25;
    useEffect(() => {
        setPage(1);
        let keys = [...selected];
        let type = 'setting';
        switch(category){
            case 'errors':
                type = 'error';
                break;

            case 'statuses':
                type = 'status';
                break;

            case 'connections':
                type = 'connection';
                break;

            case 'live':
                type = 'live';
                break;
        }

        if (keys.includes('Current')){
          let i = keys.indexOf('Current');
          keys[i] = '#cr';
        }
        if (keys.includes('Filter')){
          let i = keys.indexOf('Filter');
          keys[i] = '#fl';
        }

        const spaInfo = {guid:spa_guid, date_from:new Date(startDate).getTime(), date_to:new Date(endDate).getTime(), keys:keys, type:type};

        // Skip the (expensive, full-month) refetch when redux already holds this exact
        // report — e.g. toggling between graph and table view of the same live data.
        if (lastQuery === JSON.stringify(spaInfo) && (history.length > 0 || loading)) {
            return;
        }

        dispatch(clearSpaHistory());
        dispatch(fetchSpaHistory(spaInfo));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[dispatch, spa_guid, selected, startDate, endDate, category]);

    useEffect(() => {
        // Collect column headers in a single pass (the previous concat-in-loop was O(n^2)).
        const headerSet = new Set<string>();
        history.forEach((obj:any)=>{
            for (const key of Object.keys(obj)) headerSet.add(key);
        });
        const newArray = Array.from(headerSet)
            .filter((header:any)=>header!=='spa_guid' && header!=='timestamp')
            .sort(); //sort headers alphabetically

        newArray.push('timestamp');
        setHeaders(columns ? columns.map(c => c.key) : newArray);
    },[history,category,columns]);
    const headerLabel = (header: string) => columns?.find(c => c.key === header)?.label ?? header;

    // Derived from state rather than sorted inside the click handler: the handler
    // only saw the pre-update `order`/`orderBy`, so every click sorted by the
    // previous column/direction and the table lagged one click behind.
    const sortedRows = useMemo<any[]>(() => {
        if (!orderBy) return history;

        const dir = order === 'asc' ? 1 : -1;
        // Decorate once per row: parsing `timestamp` inside the comparator meant
        // two Date allocations per comparison over a full month of reports.
        const decorated = history.map((row:any)=>({
            row,
            t: row?.timestamp ? new Date(row.timestamp).getTime() : NaN,
            key: orderBy === 'timestamp'
                ? (row?.timestamp ? new Date(row.timestamp).getTime() : NaN)
                : row?.[orderBy],
        }));

        decorated.sort((a:any, b:any)=>{
            const aBlank = isBlank(a.key);
            const bBlank = isBlank(b.key);
            // Blanks sink to the bottom in both directions instead of bailing out
            // of the comparison entirely and leaving the rows unordered.
            if (aBlank || bBlank) return aBlank && bBlank ? 0 : aBlank ? 1 : -1;

            const cmp = compareValues(a.key, b.key);
            if (cmp !== 0) return cmp * dir;

            // Ties (common on boolean/enum columns) fall back to newest first.
            if (Number.isNaN(a.t) || Number.isNaN(b.t)) return 0;
            return b.t - a.t;
        });

        return decorated.map((x:any)=>x.row);
    },[history, order, orderBy]);

    const handleRequestSort = (header: string) => {
        const isAsc = orderBy === header && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(header);
        setPage(1);
    };

  const totalPages = Math.max(1, Math.ceil(sortedRows.length/rowsPerPage));
  const pageRows = sortedRows.slice((page-1) * rowsPerPage, (page-1) * rowsPerPage + rowsPerPage);

  const renderValue = (key: string, value: any) => {
    if (key==='timestamp') return <span className="dt-date">{formatTime(value)}</span>;
    if (typeof value === 'boolean') {
      const on = key==='SBConnected' ? 'Connected' : 'On';
      const off = key==='SBConnected' ? 'Disconnected' : 'Off';
      return value ? <span className="dt-pill good">{on}</span> : <span className="dt-off">{off}</span>;
    }
    if (key==='sbpH' || key==='Current') return <span className="dt-num">{value ? Number(value)/100 : 0}</span>;
    if (key==='P1') return PumpState[value];
    if (key==='SBConnected') return SBConState[value];
    if (key==='Filter') return filterStateMapper[value as keyof typeof filterStateMapper] ?? value;
    if (key==='H1') return ActiveCycleStyleMap[value as number];
    if (key==='phSM') return SmartPHStateTextMap[value as keyof typeof SmartPHStateTextMap];
    if (key==='sbEState') return V1ElectrodeState[value] ?? value;
    if (key==='sbEPol') return V1ElectrodePolarity[value] ?? value;
    if (key==='sbProducing' || key==='Econ') return (value || value === 1) ? <span className="dt-pill good">On</span> : <span className="dt-off">Off</span>;
    return typeof value === 'number' ? <span className="dt-num">{value}</span> : value;
  };

  // Rendered inside the report's result card (SpaReport), which supplies the toolbar
  return (
    <>
      <div className="dt-scroll">
        {loading ?
        <ReportLoadingProgress/>
        :
        sortedRows.length>0 ? (
          <table className="dt-table">
            <thead>
              <tr>
                {headers.map((header:any)=>
                  header!=='spa_guid' &&
                  <th key={header} scope="col" aria-sort={orderBy === header ? (order === 'asc' ? 'ascending' : 'descending') : undefined}>
                    <SortButton label={headerLabel(header)} active={orderBy === header} order={order} onClick={()=>handleRequestSort(header)}/>
                  </th>
                )}
              </tr>
            </thead>

            <tbody>
              {pageRows.map((row:any,i)=>
                <tr key={i}>
                  {headers.map((key:any)=>
                    key!=='spa_guid' &&
                    <td key={key}>{renderValue(key, row[key])}</td>
                  )}
                </tr>
              )}
            </tbody>
          </table>
        ):(
          <div className="dt-state"><div className="dt-empty-title">No Reports Found</div></div>
        )}
      </div>

      <DataTableFoot
        shown={pageRows.length}
        total={sortedRows.length}
        noun={sortedRows.length === 1 ? 'Report' : 'Reports'}
        page={page}
        totalPages={totalPages}
        loading={loading}
        onPage={setPage}
      />
    </>
  );
};

export default ReportsTable;
